import userNotificationHandler from '@/config/services/userNotificationHandler.js'
import { Channel } from 'src/store/models/index.js'
import { Chat, Message, Survey, CalendarEvent } from 'src/store/models/channel/index.js'
import constants from '@/config/constants.ts'
import GlobalPrototypes from '@/config/services/globalPrototypes.ts'
import { Store } from 'vuex'
import type { State } from '@/types/store/state.ts'
import type { Router } from 'vue-router'
import type { EventListener } from '@/types/services/broadcasting/BroadcastingEvents'
import BroadcastingServiceProvider from '@/config/services/broadcasting/BroadcastingServiceProvider.ts'
import {i18n} from '@/config/i18n.ts'

const HANDLER_NAME = 'global'

const globalEventListeners = (userId: number): EventListener[] => {
  return [
    // Chat message (Note: Calendar Events are also sent as chat messages)
    {
      identifier: userId,
      channelName: 'user',
      handlerName: HANDLER_NAME,
      event: {
        event: '.Illuminate\\Notifications\\Events\\BroadcastNotificationCreated',
        callback: async (data: any) => {
          if (!data) return
          const isMutedMessage = await processNotification(data)
          userNotificationHandler.notifyUser(data.notification, isMutedMessage)
        },
      },
    },

    // Sync read state across users devices:
    // Messages of a channel were read by opening the chat or the channel was manually marked as read.
    {
      identifier: userId,
      channelName: 'user',
      handlerName: HANDLER_NAME,
      event: {
        event: '.channels.read',
        callback: async (data: any) => {
          if (!data) return
          await Channel.updateChannelLocally(data.channel)
          await Channel.resetChannelLastUnreadCount(data.channel?.id)
        },
      },
    },

    // Sync unread state across users devices:
    // Channel was manually marked as unread.
    {
      identifier: userId,
      channelName: 'user',
      handlerName: HANDLER_NAME,
      event: {
        event: '.channels.unread',
        callback: async (data: any) => {
          if (!data) return
          await Channel.updateChannelLocally(data.channel)
          await Channel.resetChannelLastUnreadCount(data.channel?.id)
        },
      },
    },

    // Sync notification count (Calendar Events) across users devices:
    // Calendar Event was viewed or manually changed by marking as read/unread.
    {
      identifier: userId,
      channelName: 'user',
      handlerName: HANDLER_NAME,
      event: {
        event: '.notification.count.changed',
        callback: async (data: any) => {
          if (!data) return
          await Chat.updateNotificationCounter(data.notification?.count || 0)
        },
      },
    },
  ]
}

const fetchTargetMessage = async (msg: any) => {
  if ([constants.message.targetTypes.SURVEYS].includes(msg.target_type)) return fetchSurvey(msg)
  if (msg.target_type === constants.message.targetTypes.CONFERENCES) return fetchConference(msg)
  if (msg.target_type === constants.message.targetTypes.EVENTS) return fetchCalendarEvent(msg)
  return GlobalPrototypes.clone(msg)
}

const fetchConference = async (msg: any): Promise<any> => {
  const res = await Message.getConference(msg.channel_id, msg.target.uuid)
  return { ...msg, target: res.data }
}

const fetchSurvey = async (msg: any): Promise<any> => {
  const res = await Survey.getDetails(msg.target.id)
  return { ...msg, target: res.data }
}

const fetchCalendarEvent = async (msg: any): Promise<any> => {
  if (!msg.target) return msg
  const res = await CalendarEvent.getCalendarEventById(msg.target.id)
  return {
    ...msg,
    target: res.data,
    target_snapshot: {
      id: res.data.id,
      name: res.data.name,
      begins_at: res.data.begins_at,
      user: res.data.user,
    },
  }
}

const updateNotificationCounter = async (store: Store<State>, type: string, unreadCount: number): Promise<void> => {
  if ([constants.message.types.MESSAGE, constants.message.types.VOICE, constants.message.types.KNOCK].includes(type)) {
    return await store.dispatch('channel/updateUnreadCount', unreadCount)
  }
  if (constants.message.types.NC_EVENT === type) {
    return await store.dispatch('notification/updateUnreadNotificationCount', unreadCount)
  }
}

const processChatMessageSentNotification = async (notification: any) => {
  const messageData = notification.message // TODO Type this
  const notificationData = notification.notification

  let isMutedMessage = false
  if (GlobalPrototypes.router) {
    const router = GlobalPrototypes.router as Router
    // Determine if message should trigger an InApp-notification (toast)
    isMutedMessage =
      router.currentRoute.value.name === 'channel-single-chat' && Number(router.currentRoute.value.params.id) === messageData.channel_id
  } else {
    throw new Error('[Global Broadcasting Handler] GlobalPrototypes.router is not defined!')
  }

  // prevent further actions if message is already in store
  // happens when new message is broadcasted to author
  const existingMsg = Message.find(messageData.uuid)
  if (existingMsg) return isMutedMessage

  // fetch target of message since notification only return a reduced response object
  const newMsg = await fetchTargetMessage(messageData)

  // Update $Message and its $Channel
  await Message.insertOrUpdate({ data: GlobalPrototypes.clone(newMsg) })

  // Only update channel if message is not a NC_EVENT (we don't want to store personal-notifications channel in $store)
  if (constants.message.types.NC_EVENT !== messageData.type) {
    await Channel.updateManually(messageData.channel_id, messageData)
  }

  if (GlobalPrototypes.store) {
    const store = GlobalPrototypes.store as Store<State>

    // Update aria-live status message if we're viewing the chat of the channel (isMutedMessage is true in that case)
    isMutedMessage && store.commit('chat/ADD_ARIA_LIVE_STATUS_MESSAGE', i18n.global.t('chats.channel.status.message_received'))

    // Update notification unread counter
    const oldUnreadCount = parseInt(store.getters['notification/UNREAD_NOTIFICATION_COUNTER'] || 0)
    await updateNotificationCounter(store, messageData.type, oldUnreadCount + parseInt(notificationData.unread_count))
  } else {
    throw new Error('[Global Broadcasting Handler] GlobalPrototypes.store is not defined!')
  }

  return isMutedMessage
}

const processNotification = async (notification: any) => {
  if (notification.type === 'chat.message.sent') {
    return await processChatMessageSentNotification(notification)
  }
  // Add additional global notification types here if needed
}

export const startGlobalBroadcastingListeners = (userId: number) => {
  if (!userId) return

  const service = BroadcastingServiceProvider.getService()
  if (!service) return

  globalEventListeners(userId).forEach((listener) => {
    service.addEventListener(listener)
  })
}

export const stopGlobalBroadcastingListeners = (userId: number) => {
  if (!userId) return

  const service = BroadcastingServiceProvider.getService()
  if (!service) return

  globalEventListeners(userId).forEach((listener) => {
    service.removeEventListener(listener)
  })
}
